import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, TaskTimeEntry, TaskTimeSummary, TimeReportSummary, TaskComment, TaskAttachment } from '../lib/api'
import { useDatePreferences } from '../hooks/useDatePreferences'
import {
  ClipboardList,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Clock,
  CheckCircle,
  User,
  FileText,
  Calendar,
  Plus,
  X,
  Edit2,
  Trash2,
  LinkIcon,
  Unlink,
  XCircle,
  PlayCircle,
  Shield,
  Building2,
  File,
  AlertTriangle,
  Square,
  CheckSquare,
  MinusSquare,
  UserPlus,
  MoreHorizontal,
  List,
  CalendarDays,
  RefreshCw,
  Timer,
  TimerOff,
  Play,
  Pause,
  BarChart3,
  Download,
  // Approval workflow icons (Issue #150)
  Send,
  ThumbsUp,
  ThumbsDown,
  History,
  ShieldCheck,
  UserCheck,
  // Kanban view icon (Issue #107)
  LayoutGrid,
  // Task comments icon (Issue #90)
  MessageSquare,
  // Task attachments icons (Issue #98)
  Paperclip,
  Upload,
  // Jira sync icon (Atlassian integration)
  ExternalLink,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import TaskCalendar from '../components/TaskCalendar'
import TaskKanban from '../components/TaskKanban'

// ============================================================================
// TYPES
// ============================================================================

interface Task {
  id: string
  title: string
  description?: string
  type: 'evidence-due' | 'document-review' | 'risk-assessment' | 'vendor-assessment' | 'custom'
  assigned_to?: string
  assigned_to_username?: string
  assigned_to_email?: string
  created_by: string
  created_by_username?: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'open' | 'in-progress' | 'pending-approval' | 'approved' | 'rejected' | 'completed' | 'cancelled'
  due_date?: number
  estimated_hours?: number
  related_control_id?: string
  control_number?: string
  control_name?: string
  related_document_id?: string
  document_title?: string
  related_risk_id?: string
  risk_title?: string
  related_vendor_id?: string
  vendor_name?: string
  evidence_count?: number
  source?: 'manual' | 'auto-frequency' | 'auto-reminder'
  auto_task_period?: string
  // Approval workflow fields (Issue #150)
  approval_required?: number
  approver_id?: string
  approver_username?: string
  approver_role?: 'admin' | 'contributor'
  approval_history_count?: number
  // Jira sync fields (Atlassian integration)
  jira_issue_key?: string
  jira_issue_url?: string
  jira_sync_status?: 'synced' | 'pending' | 'error'
  jira_last_sync_at?: number
  created_at: number
  updated_at: number
  completed_at?: number
}

interface TaskApproval {
  id: string
  task_id: string
  approver_id: string
  approver_username?: string
  approver_email?: string
  action: 'submitted' | 'approved' | 'rejected' | 'reverted'
  comments?: string
  created_at: number
}

interface UserOption {
  id: string
  username: string
  email: string
}

interface Control {
  id: string
  control_number: string
  name: string
  framework_id: string
}

interface Evidence {
  id: string
  note?: string
  file_name?: string
  timestamp: number
  control_id: string
}

interface Counts {
  total: number
  open: number
  in_progress: number
  completed: number
  cancelled: number
  overdue: number
}

// ============================================================================
// CONSTANTS
// ============================================================================

const priorityConfig: Record<string, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-200' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  low: { label: 'Low', color: 'bg-gray-100 text-gray-800 border-gray-200' },
}

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  'in-progress': { label: 'In Progress', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: PlayCircle },
  // Approval workflow statuses (Issue #150)
  'pending-approval': { label: 'Pending Approval', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Send },
  approved: { label: 'Approved', color: 'bg-teal-100 text-teal-800 border-teal-200', icon: ThumbsUp },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200', icon: ThumbsDown },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle },
}

const typeConfig: Record<string, { label: string; icon: any }> = {
  'evidence-due': { label: 'Evidence Due', icon: FileText },
  'document-review': { label: 'Document Review', icon: File },
  'risk-assessment': { label: 'Risk Assessment', icon: AlertTriangle },
  'vendor-assessment': { label: 'Vendor Assessment', icon: Building2 },
  custom: { label: 'Custom', icon: ClipboardList },
}

// Note: formatDate and formatDateTime are now defined inside the component
// to use user preferences via useDatePreferences hook

const isOverdue = (task: Task) => {
  if (!task.due_date) return false
  if (task.status === 'completed' || task.status === 'cancelled') return false
  return task.due_date < Date.now()
}

// Format duration in hours and minutes
const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

// Format elapsed time since timestamp
const formatElapsed = (startedAt: number) => {
  const elapsed = Math.floor((Date.now() - startedAt) / 1000)
  const hours = Math.floor(elapsed / 3600)
  const mins = Math.floor((elapsed % 3600) / 60)
  const secs = elapsed % 60
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Tasks() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const { formatDate: formatDatePref, formatDateTime: formatDateTimePref } = useDatePreferences()

  // Date formatting functions using user preferences
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '-'
    return formatDatePref(timestamp)
  }

  const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return '-'
    return formatDateTimePref(timestamp)
  }

  // View state (list, calendar, or kanban)
  const [viewMode, setViewMode] = useState<'list' | 'calendar' | 'kanban'>(
    (searchParams.get('view') as 'list' | 'calendar' | 'kanban') || 'list'
  )

  // Task list state
  const [tasks, setTasks] = useState<Task[]>([])
  const [counts, setCounts] = useState<Counts>({ total: 0, open: 0, in_progress: 0, completed: 0, cancelled: 0, overdue: 0 })
  const [loading, setLoading] = useState(true)

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '')
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || '')
  const [assignedFilter, setAssignedFilter] = useState(searchParams.get('assigned_to') || '')
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || '')
  const [showFilters, setShowFilters] = useState(false)

  // Reference data
  const [users, setUsers] = useState<UserOption[]>([])
  const [controls, setControls] = useState<Control[]>([])
  const [evidence, setEvidence] = useState<Evidence[]>([])

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showLinkEvidenceModal, setShowLinkEvidenceModal] = useState(false)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [linkedEvidence, setLinkedEvidence] = useState<any[]>([])

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'custom' as Task['type'],
    assigned_to: '',
    priority: 'medium' as Task['priority'],
    due_date: '',
    related_control_id: '',
    estimated_hours: '',
    // Approval workflow fields (Issue #150)
    approval_required: false,
    approver_id: '',
    approver_role: '' as '' | 'admin' | 'contributor',
  })
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)

  // Time tracking state
  const [activeTimer, setActiveTimer] = useState<(TaskTimeEntry & { task_title?: string }) | null>(null)
  const [timerElapsed, setTimerElapsed] = useState('')
  const [showTimeModal, setShowTimeModal] = useState(false)
  const [showTimeReportModal, setShowTimeReportModal] = useState(false)
  const [timeEntries, setTimeEntries] = useState<TaskTimeEntry[]>([])
  const [timeSummary, setTimeSummary] = useState<TaskTimeSummary | null>(null)
  const [timeReport, setTimeReport] = useState<TimeReportSummary | null>(null)
  const [timeReportFilters, setTimeReportFilters] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
    user_id: '',
  })
  const [timeEntryForm, setTimeEntryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: 0,
    minutes: 0,
    description: '',
  })
  const timerInterval = useRef<NodeJS.Timeout | null>(null)

  // Bulk selection state
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set())
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false)
  const [bulkActionLoading, setBulkActionLoading] = useState(false)
  const [bulkActionError, setBulkActionError] = useState('')

  // Approval workflow state (Issue #150)
  const [pendingApprovalTasks, setPendingApprovalTasks] = useState<Task[]>([])
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showApprovalHistoryModal, setShowApprovalHistoryModal] = useState(false)
  const [taskApprovals, setTaskApprovals] = useState<TaskApproval[]>([])
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | null>(null)
  const [approvalComments, setApprovalComments] = useState('')
  const [autoCompleteOnApproval, setAutoCompleteOnApproval] = useState(true)
  const [approvalLoading, setApprovalLoading] = useState(false)

  // Task comments state (Issue #90)
  const [showCommentsModal, setShowCommentsModal] = useState(false)
  const [taskComments, setTaskComments] = useState<TaskComment[]>([])
  const [newComment, setNewComment] = useState('')
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentContent, setEditingCommentContent] = useState('')
  const [commentsLoading, setCommentsLoading] = useState(false)

  // Task attachments state (Issue #98)
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false)
  const [taskAttachments, setTaskAttachments] = useState<TaskAttachment[]>([])
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [attachmentUploading, setAttachmentUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Fetch users
  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await api.getUsers()
        if (res.success) {
          setUsers(res.users)
        }
      } catch (err) {
        console.error('Failed to load users:', err)
      }
    }
    fetchUsers()
  }, [])

  // Fetch controls
  useEffect(() => {
    async function fetchControls() {
      try {
        const res = await api.getControls({ limit: 500 })
        if (res.success) {
          setControls(res.controls)
        }
      } catch (err) {
        console.error('Failed to load controls:', err)
      }
    }
    fetchControls()
  }, [])

  // Fetch tasks - extracted as useCallback so it can be called from approval handlers
  const fetchTasks = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.getTasksList({
        status: statusFilter as any || undefined,
        type: typeFilter as any || undefined,
        priority: priorityFilter as any || undefined,
        assigned_to: assignedFilter || undefined,
        source: sourceFilter as any || undefined,
        search: search || undefined,
        limit: 200,
      })
      if (res.success) {
        setTasks(res.tasks as Task[])
        setCounts(res.counts)
      }
    } catch (err) {
      console.error('Failed to load tasks:', err)
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, typeFilter, priorityFilter, assignedFilter, sourceFilter])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  // Fetch active timer
  useEffect(() => {
    async function fetchActiveTimer() {
      try {
        const res = await api.getMyTimer()
        if (res.success && res.timer) {
          setActiveTimer(res.timer)
        }
      } catch (err) {
        console.error('Failed to fetch active timer:', err)
      }
    }
    fetchActiveTimer()
  }, [])

  // Timer tick effect
  useEffect(() => {
    if (activeTimer && !activeTimer.ended_at) {
      // Update elapsed time every second
      const updateElapsed = () => {
        setTimerElapsed(formatElapsed(activeTimer.started_at))
      }
      updateElapsed()
      timerInterval.current = setInterval(updateElapsed, 1000)
      return () => {
        if (timerInterval.current) {
          clearInterval(timerInterval.current)
        }
      }
    } else {
      setTimerElapsed('')
      if (timerInterval.current) {
        clearInterval(timerInterval.current)
      }
    }
  }, [activeTimer])

  // Fetch pending approval tasks (Issue #150)
  const fetchPendingApprovals = useCallback(async () => {
    try {
      const res = await api.getTasksPendingApproval()
      if (res.success) {
        setPendingApprovalTasks(res.tasks as Task[])
        setPendingApprovalCount(res.count)
      }
    } catch (err) {
      console.error('Failed to fetch pending approval tasks:', err)
    }
  }, [])

  useEffect(() => {
    fetchPendingApprovals()
  }, [fetchPendingApprovals])

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSearch = (value: string) => {
    setSearch(value)
    const params = new URLSearchParams(searchParams)
    if (value) params.set('search', value)
    else params.delete('search')
    setSearchParams(params)
  }

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value)
    const params = new URLSearchParams(searchParams)
    if (value) params.set('status', value)
    else params.delete('status')
    setSearchParams(params)
  }

  const handleViewModeChange = (mode: 'list' | 'calendar' | 'kanban') => {
    setViewMode(mode)
    const params = new URLSearchParams(searchParams)
    if (mode === 'list') params.delete('view')
    else params.set('view', mode)
    setSearchParams(params)
  }

  const handleOpenCreate = () => {
    setFormData({
      title: '',
      description: '',
      type: 'custom',
      assigned_to: '',
      priority: 'medium',
      due_date: '',
      related_control_id: '',
      estimated_hours: '',
      // Approval workflow fields (Issue #150)
      approval_required: false,
      approver_id: '',
      approver_role: '',
    })
    setFormError('')
    setShowCreateModal(true)
  }

  const handleOpenEdit = (task: Task) => {
    setSelectedTask(task)
    setFormData({
      title: task.title,
      description: task.description || '',
      type: task.type,
      assigned_to: task.assigned_to || '',
      priority: task.priority,
      due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
      related_control_id: task.related_control_id || '',
      estimated_hours: task.estimated_hours?.toString() || '',
      // Approval workflow fields (Issue #150)
      approval_required: task.approval_required === 1,
      approver_id: task.approver_id || '',
      approver_role: task.approver_role || '',
    })
    setFormError('')
    setShowEditModal(true)
  }

  const handleOpenDelete = (task: Task) => {
    setSelectedTask(task)
    setShowDeleteConfirm(true)
  }

  const handleOpenLinkEvidence = async (task: Task) => {
    setSelectedTask(task)
    try {
      // Fetch available evidence
      const evidenceRes = await api.getEvidence({ limit: 200 })
      if (evidenceRes.success) {
        setEvidence(evidenceRes.evidence)
      }
      // Fetch already linked evidence
      const linkedRes = await api.getTaskEvidence(task.id)
      if (linkedRes.success) {
        setLinkedEvidence(linkedRes.evidence)
      }
    } catch (err) {
      console.error('Failed to load evidence:', err)
    }
    setShowLinkEvidenceModal(true)
  }

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      setFormError('Title is required')
      return
    }

    setSaving(true)
    setFormError('')

    try {
      const res = await api.createTask({
        title: formData.title,
        description: formData.description || undefined,
        type: formData.type,
        assigned_to: formData.assigned_to || undefined,
        priority: formData.priority,
        due_date: formData.due_date ? new Date(formData.due_date).getTime() : undefined,
        related_control_id: formData.related_control_id || undefined,
        // Approval workflow fields (Issue #150)
        approval_required: formData.approval_required,
        approver_id: formData.approver_id || undefined,
        approver_role: formData.approver_role || undefined,
      })

      if (res.success) {
        setShowCreateModal(false)
        // Refresh tasks
        const tasksRes = await api.getTasksList({ limit: 200 })
        if (tasksRes.success) {
          setTasks(tasksRes.tasks)
          setCounts(tasksRes.counts)
        }
      } else {
        setFormError('Failed to create task')
      }
    } catch (err: any) {
      setFormError(err.message || 'Failed to create task')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async () => {
    if (!selectedTask) return
    if (!formData.title.trim()) {
      setFormError('Title is required')
      return
    }

    setSaving(true)
    setFormError('')

    try {
      const res = await api.updateTask(selectedTask.id, {
        title: formData.title,
        description: formData.description || undefined,
        type: formData.type,
        assigned_to: formData.assigned_to || null,
        priority: formData.priority,
        due_date: formData.due_date ? new Date(formData.due_date).getTime() : null,
        related_control_id: formData.related_control_id || null,
      })

      if (res.success) {
        setShowEditModal(false)
        setSelectedTask(null)
        // Refresh tasks
        const tasksRes = await api.getTasksList({ limit: 200 })
        if (tasksRes.success) {
          setTasks(tasksRes.tasks)
          setCounts(tasksRes.counts)
        }
      } else {
        setFormError('Failed to update task')
      }
    } catch (err: any) {
      setFormError(err.message || 'Failed to update task')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedTask) return

    setSaving(true)
    try {
      const res = await api.deleteTask(selectedTask.id)
      if (res.success) {
        setShowDeleteConfirm(false)
        setSelectedTask(null)
        // Refresh tasks
        const tasksRes = await api.getTasksList({ limit: 200 })
        if (tasksRes.success) {
          setTasks(tasksRes.tasks)
          setCounts(tasksRes.counts)
        }
      }
    } catch (err) {
      console.error('Failed to delete task:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = async (task: Task, newStatus: Task['status']) => {
    try {
      const res = await api.updateTask(task.id, { status: newStatus })
      if (res.success) {
        // Refresh tasks
        const tasksRes = await api.getTasksList({ limit: 200 })
        if (tasksRes.success) {
          setTasks(tasksRes.tasks)
          setCounts(tasksRes.counts)
        }
      }
    } catch (err) {
      console.error('Failed to update task status:', err)
    }
  }

  const handleLinkEvidence = async (evidenceId: string) => {
    if (!selectedTask) return

    try {
      const res = await api.linkTaskEvidence(selectedTask.id, evidenceId)
      if (res.success) {
        // Refresh linked evidence
        const linkedRes = await api.getTaskEvidence(selectedTask.id)
        if (linkedRes.success) {
          setLinkedEvidence(linkedRes.evidence)
        }
        // Refresh task list to update evidence count
        const tasksRes = await api.getTasksList({ limit: 200 })
        if (tasksRes.success) {
          setTasks(tasksRes.tasks)
        }
      }
    } catch (err) {
      console.error('Failed to link evidence:', err)
    }
  }

  const handleUnlinkEvidence = async (evidenceId: string) => {
    if (!selectedTask) return

    try {
      const res = await api.unlinkTaskEvidence(selectedTask.id, evidenceId)
      if (res.success) {
        setLinkedEvidence(linkedEvidence.filter(e => e.evidence_id !== evidenceId))
        // Refresh task list to update evidence count
        const tasksRes = await api.getTasksList({ limit: 200 })
        if (tasksRes.success) {
          setTasks(tasksRes.tasks)
        }
      }
    } catch (err) {
      console.error('Failed to unlink evidence:', err)
    }
  }

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  const handleToggleTaskSelection = (taskId: string) => {
    setSelectedTasks(prev => {
      const next = new Set(prev)
      if (next.has(taskId)) {
        next.delete(taskId)
      } else {
        next.add(taskId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      // Deselect all
      setSelectedTasks(new Set())
    } else {
      // Select all visible tasks
      setSelectedTasks(new Set(tasks.map(t => t.id)))
    }
  }

  const clearSelection = () => {
    setSelectedTasks(new Set())
    setBulkActionError('')
  }

  const refreshTasks = async () => {
    try {
      const res = await api.getTasksList({
        status: statusFilter as any || undefined,
        type: typeFilter as any || undefined,
        priority: priorityFilter as any || undefined,
        assigned_to: assignedFilter || undefined,
        search: search || undefined,
        limit: 200,
      })
      if (res.success) {
        setTasks(res.tasks)
        setCounts(res.counts)
      }
    } catch (err) {
      console.error('Failed to refresh tasks:', err)
    }
  }

  const handleBulkStatusUpdate = async (newStatus: Task['status']) => {
    if (selectedTasks.size === 0) return

    setBulkActionLoading(true)
    setBulkActionError('')

    try {
      const res = await api.bulkUpdateTasks(
        Array.from(selectedTasks),
        { status: newStatus }
      )

      if (res.success) {
        clearSelection()
        await refreshTasks()
      } else {
        setBulkActionError('Failed to update tasks')
      }
    } catch (err: any) {
      console.error('Bulk status update failed:', err)
      setBulkActionError(err.message || 'Failed to update tasks')
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkPriorityUpdate = async (newPriority: Task['priority']) => {
    if (selectedTasks.size === 0) return

    setBulkActionLoading(true)
    setBulkActionError('')

    try {
      const res = await api.bulkUpdateTasks(
        Array.from(selectedTasks),
        { priority: newPriority }
      )

      if (res.success) {
        clearSelection()
        await refreshTasks()
      } else {
        setBulkActionError('Failed to update tasks')
      }
    } catch (err: any) {
      console.error('Bulk priority update failed:', err)
      setBulkActionError(err.message || 'Failed to update tasks')
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkAssign = async (userId: string | null) => {
    if (selectedTasks.size === 0) return

    setBulkActionLoading(true)
    setBulkActionError('')

    try {
      const res = await api.bulkUpdateTasks(
        Array.from(selectedTasks),
        { assigned_to: userId }
      )

      if (res.success) {
        clearSelection()
        setShowBulkAssignModal(false)
        await refreshTasks()
      } else {
        setBulkActionError('Failed to assign tasks')
      }
    } catch (err: any) {
      console.error('Bulk assign failed:', err)
      setBulkActionError(err.message || 'Failed to assign tasks')
    } finally {
      setBulkActionLoading(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedTasks.size === 0) return

    setBulkActionLoading(true)
    setBulkActionError('')

    try {
      const res = await api.bulkDeleteTasks(Array.from(selectedTasks))

      if (res.success) {
        clearSelection()
        setShowBulkDeleteConfirm(false)
        await refreshTasks()

        // Show warning if some tasks couldn't be deleted
        if (res.warnings) {
          const warnings: string[] = []
          if (res.warnings.not_found && res.warnings.not_found.length > 0) {
            warnings.push(`${res.warnings.not_found.length} task(s) not found`)
          }
          if (res.warnings.unauthorized && res.warnings.unauthorized.length > 0) {
            warnings.push(`${res.warnings.unauthorized.length} task(s) - no permission`)
          }
          if (warnings.length > 0) {
            setBulkActionError(`Deleted ${res.deleted_count} task(s). Warnings: ${warnings.join(', ')}`)
          }
        }
      } else {
        setBulkActionError('Failed to delete tasks')
      }
    } catch (err: any) {
      console.error('Bulk delete failed:', err)
      setBulkActionError(err.message || 'Failed to delete tasks')
    } finally {
      setBulkActionLoading(false)
    }
  }

  // Clear selection when filters change
  useEffect(() => {
    setSelectedTasks(new Set())
  }, [search, statusFilter, typeFilter, priorityFilter, assignedFilter])

  // ============================================================================
  // TIME TRACKING HANDLERS
  // ============================================================================

  const handleStartTimer = async (task: Task) => {
    try {
      const res = await api.startTaskTimer(task.id)
      if (res.success) {
        setActiveTimer({
          id: res.timer.entry_id,
          tenant_id: '',
          task_id: res.timer.task_id,
          user_id: user?.id || '',
          started_at: res.timer.started_at,
          entry_type: 'timer',
          created_at: res.timer.started_at,
          updated_at: res.timer.started_at,
          task_title: task.title,
        })
      }
    } catch (err: any) {
      console.error('Failed to start timer:', err)
      alert(err.message || 'Failed to start timer')
    }
  }

  const handleStopTimer = async (description?: string) => {
    if (!activeTimer) return

    try {
      const res = await api.stopTaskTimer(activeTimer.task_id, description)
      if (res.success) {
        setActiveTimer(null)
        // If viewing time entries for this task, refresh
        if (selectedTask?.id === activeTimer.task_id) {
          fetchTimeEntries(activeTimer.task_id)
        }
      }
    } catch (err: any) {
      console.error('Failed to stop timer:', err)
      alert(err.message || 'Failed to stop timer')
    }
  }

  const fetchTimeEntries = async (taskId: string) => {
    try {
      const res = await api.getTaskTimeEntries(taskId)
      if (res.success) {
        setTimeEntries(res.time_entries)
        setTimeSummary(res.summary)
      }
    } catch (err) {
      console.error('Failed to fetch time entries:', err)
    }
  }

  const handleOpenTimeModal = async (task: Task) => {
    setSelectedTask(task)
    await fetchTimeEntries(task.id)
    setTimeEntryForm({
      date: new Date().toISOString().split('T')[0],
      hours: 0,
      minutes: 0,
      description: '',
    })
    setShowTimeModal(true)
  }

  const handleAddTimeEntry = async () => {
    if (!selectedTask) return
    if (timeEntryForm.hours === 0 && timeEntryForm.minutes === 0) {
      alert('Please enter a duration')
      return
    }

    try {
      const res = await api.createTaskTimeEntry(selectedTask.id, {
        date: timeEntryForm.date,
        hours: timeEntryForm.hours,
        minutes: timeEntryForm.minutes,
        description: timeEntryForm.description || undefined,
      })
      if (res.success) {
        await fetchTimeEntries(selectedTask.id)
        setTimeEntryForm({
          date: new Date().toISOString().split('T')[0],
          hours: 0,
          minutes: 0,
          description: '',
        })
      }
    } catch (err: any) {
      console.error('Failed to add time entry:', err)
      alert(err.message || 'Failed to add time entry')
    }
  }

  const handleDeleteTimeEntry = async (entryId: string) => {
    if (!selectedTask) return
    if (!confirm('Delete this time entry?')) return

    try {
      const res = await api.deleteTaskTimeEntry(selectedTask.id, entryId)
      if (res.success) {
        await fetchTimeEntries(selectedTask.id)
      }
    } catch (err: any) {
      console.error('Failed to delete time entry:', err)
      alert(err.message || 'Failed to delete time entry')
    }
  }

  const handleOpenTimeReport = async () => {
    try {
      const res = await api.getTimeReport(timeReportFilters)
      if (res.success) {
        setTimeReport(res.summary)
      }
    } catch (err) {
      console.error('Failed to fetch time report:', err)
    }
    setShowTimeReportModal(true)
  }

  const handleExportTimeReport = () => {
    if (!timeReport) return

    // Create CSV content
    const headers = ['Task', 'Status', 'Estimated Hours', 'Actual Hours', 'Variance', 'Entries']
    const rows = timeReport.by_task.map(t => [
      t.task_title,
      t.task_status,
      t.estimated_hours?.toString() || '',
      t.total_hours.toString(),
      t.variance_hours !== null ? t.variance_hours.toString() : '',
      t.entry_count.toString(),
    ])

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n')

    // Download
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `time-report-${timeReportFilters.start}-to-${timeReportFilters.end}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ============================================================================
  // APPROVAL WORKFLOW HANDLERS (Issue #150)
  // ============================================================================

  const handleSubmitForApproval = async (task: Task) => {
    try {
      setApprovalLoading(true)
      const res = await api.submitTaskForApproval(task.id)
      if (res.success) {
        // Refresh tasks and pending approvals
        await fetchTasks()
        await fetchPendingApprovals()
      }
    } catch (err: any) {
      console.error('Failed to submit for approval:', err)
      alert(err.message || 'Failed to submit task for approval')
    } finally {
      setApprovalLoading(false)
    }
  }

  const handleOpenApprovalModal = (task: Task, action: 'approve' | 'reject') => {
    setSelectedTask(task)
    setApprovalAction(action)
    setApprovalComments('')
    setAutoCompleteOnApproval(true)
    setShowApprovalModal(true)
  }

  const handleApproveTask = async () => {
    if (!selectedTask) return

    try {
      setApprovalLoading(true)
      const res = await api.approveTask(selectedTask.id, {
        comments: approvalComments || undefined,
        auto_complete: autoCompleteOnApproval,
      })
      if (res.success) {
        setShowApprovalModal(false)
        setSelectedTask(null)
        setApprovalComments('')
        // Refresh tasks and pending approvals
        await fetchTasks()
        await fetchPendingApprovals()
      }
    } catch (err: any) {
      console.error('Failed to approve task:', err)
      alert(err.message || 'Failed to approve task')
    } finally {
      setApprovalLoading(false)
    }
  }

  const handleRejectTask = async () => {
    if (!selectedTask) return
    if (!approvalComments.trim()) {
      alert('Please provide a reason for rejection')
      return
    }

    try {
      setApprovalLoading(true)
      const res = await api.rejectTask(selectedTask.id, approvalComments)
      if (res.success) {
        setShowApprovalModal(false)
        setSelectedTask(null)
        setApprovalComments('')
        // Refresh tasks and pending approvals
        await fetchTasks()
        await fetchPendingApprovals()
      }
    } catch (err: any) {
      console.error('Failed to reject task:', err)
      alert(err.message || 'Failed to reject task')
    } finally {
      setApprovalLoading(false)
    }
  }

  const handleViewApprovalHistory = async (task: Task) => {
    try {
      setSelectedTask(task)
      const res = await api.getTaskApprovals(task.id)
      if (res.success) {
        setTaskApprovals(res.approvals as TaskApproval[])
        setShowApprovalHistoryModal(true)
      }
    } catch (err) {
      console.error('Failed to fetch approval history:', err)
    }
  }

  // ============================================================================
  // TASK COMMENTS HANDLERS (Issue #90)
  // ============================================================================

  const handleOpenComments = async (task: Task) => {
    setSelectedTask(task)
    setCommentsLoading(true)
    try {
      const res = await api.getTaskComments(task.id)
      if (res.success) {
        setTaskComments(res.comments)
        setShowCommentsModal(true)
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err)
    } finally {
      setCommentsLoading(false)
    }
  }

  const handleAddComment = async () => {
    if (!selectedTask || !newComment.trim()) return

    setCommentsLoading(true)
    try {
      const res = await api.addTaskComment(selectedTask.id, newComment.trim())
      if (res.success) {
        setTaskComments([...taskComments, res.comment])
        setNewComment('')
      }
    } catch (err) {
      console.error('Failed to add comment:', err)
    } finally {
      setCommentsLoading(false)
    }
  }

  const handleStartEditComment = (comment: TaskComment) => {
    setEditingCommentId(comment.id)
    setEditingCommentContent(comment.content)
  }

  const handleCancelEditComment = () => {
    setEditingCommentId(null)
    setEditingCommentContent('')
  }

  const handleSaveEditComment = async (commentId: string) => {
    if (!selectedTask || !editingCommentContent.trim()) return

    setCommentsLoading(true)
    try {
      const res = await api.updateTaskComment(selectedTask.id, commentId, editingCommentContent.trim())
      if (res.success) {
        setTaskComments(taskComments.map(c => c.id === commentId ? res.comment : c))
        setEditingCommentId(null)
        setEditingCommentContent('')
      }
    } catch (err) {
      console.error('Failed to update comment:', err)
    } finally {
      setCommentsLoading(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!selectedTask) return

    setCommentsLoading(true)
    try {
      const res = await api.deleteTaskComment(selectedTask.id, commentId)
      if (res.success) {
        setTaskComments(taskComments.filter(c => c.id !== commentId))
      }
    } catch (err) {
      console.error('Failed to delete comment:', err)
    } finally {
      setCommentsLoading(false)
    }
  }

  const formatCommentTime = (timestamp: number) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  const getAuthorDisplayName = (comment: TaskComment) => {
    if (comment.author_first_name || comment.author_last_name) {
      return `${comment.author_first_name || ''} ${comment.author_last_name || ''}`.trim()
    }
    return comment.author_username || 'Unknown'
  }

  const getAuthorInitials = (comment: TaskComment) => {
    if (comment.author_first_name && comment.author_last_name) {
      return `${comment.author_first_name[0]}${comment.author_last_name[0]}`.toUpperCase()
    }
    if (comment.author_username) {
      return comment.author_username.substring(0, 2).toUpperCase()
    }
    return '?'
  }

  // ============================================================================
  // TASK ATTACHMENTS HANDLERS (Issue #98)
  // ============================================================================

  const handleOpenAttachments = async (task: Task) => {
    setSelectedTask(task)
    setShowAttachmentsModal(true)
    setAttachmentsLoading(true)
    try {
      const res = await api.getTaskAttachments(task.id)
      if (res.success) {
        setTaskAttachments(res.attachments)
      }
    } catch (err) {
      console.error('Failed to fetch attachments:', err)
    } finally {
      setAttachmentsLoading(false)
    }
  }

  const handleUploadAttachment = async (files: FileList | null) => {
    if (!files || files.length === 0 || !selectedTask) return

    setAttachmentUploading(true)
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData()
        formData.append('file', file)

        const res = await api.uploadTaskAttachment(selectedTask.id, formData)
        if (res.success) {
          setTaskAttachments(prev => [res.attachment, ...prev])
        }
      }
    } catch (err) {
      console.error('Failed to upload attachment:', err)
    } finally {
      setAttachmentUploading(false)
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = ''
      }
    }
  }

  const handleDownloadAttachment = async (attachment: TaskAttachment) => {
    if (!selectedTask) return
    try {
      await api.downloadTaskAttachment(selectedTask.id, attachment.id, attachment.filename)
    } catch (err) {
      console.error('Failed to download attachment:', err)
    }
  }

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!selectedTask) return
    setAttachmentsLoading(true)
    try {
      const res = await api.deleteTaskAttachment(selectedTask.id, attachmentId)
      if (res.success) {
        setTaskAttachments(taskAttachments.filter(a => a.id !== attachmentId))
      }
    } catch (err) {
      console.error('Failed to delete attachment:', err)
    } finally {
      setAttachmentsLoading(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleUploadAttachment(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getFileIcon = (mimeType: string | null) => {
    if (!mimeType) return File
    if (mimeType.startsWith('image/')) return FileText
    if (mimeType.includes('pdf')) return FileText
    if (mimeType.includes('word') || mimeType.includes('document')) return FileText
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return FileText
    return File
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-gray-500">Manage compliance tasks and activities</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={() => handleViewModeChange('list')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="List View"
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => handleViewModeChange('kanban')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Kanban Board"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Kanban</span>
            </button>
            <button
              onClick={() => handleViewModeChange('calendar')}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title="Calendar View"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Calendar</span>
            </button>
          </div>
          {/* Time Report Button */}
          <button
            onClick={handleOpenTimeReport}
            className="btn-secondary"
            title="Time Report"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Time Report</span>
          </button>
          <button
            onClick={handleOpenCreate}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Task
          </button>
        </div>
      </div>

      {/* Active Timer Banner */}
      {activeTimer && (
        <div className="card p-4 bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-full animate-pulse">
                <Timer className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-primary-900">Timer Running</p>
                <p className="text-sm text-primary-700">
                  {activeTimer.task_title || 'Task'} - <span className="font-mono">{timerElapsed}</span>
                </p>
              </div>
            </div>
            <button
              onClick={() => handleStopTimer()}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <Pause className="h-4 w-4" />
              Stop Timer
            </button>
          </div>
        </div>
      )}

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <button
          onClick={() => handleStatusFilter('')}
          className={`card p-4 text-left transition-all ${statusFilter === '' ? 'ring-2 ring-primary-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <ClipboardList className="h-8 w-8 text-gray-400" />
            <span className="text-2xl font-bold text-gray-900">{counts.total}</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">All Tasks</p>
        </button>

        <button
          onClick={() => handleStatusFilter('open')}
          className={`card p-4 text-left transition-all ${statusFilter === 'open' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <Clock className="h-8 w-8 text-blue-500" />
            <span className="text-2xl font-bold text-blue-600">{counts.open}</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">Open</p>
        </button>

        <button
          onClick={() => handleStatusFilter('in-progress')}
          className={`card p-4 text-left transition-all ${statusFilter === 'in-progress' ? 'ring-2 ring-amber-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <PlayCircle className="h-8 w-8 text-amber-500" />
            <span className="text-2xl font-bold text-amber-600">{counts.in_progress}</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">In Progress</p>
        </button>

        <button
          onClick={() => handleStatusFilter('completed')}
          className={`card p-4 text-left transition-all ${statusFilter === 'completed' ? 'ring-2 ring-green-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <span className="text-2xl font-bold text-green-600">{counts.completed}</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">Completed</p>
        </button>

        <button
          onClick={() => handleStatusFilter('cancelled')}
          className={`card p-4 text-left transition-all ${statusFilter === 'cancelled' ? 'ring-2 ring-gray-400' : ''}`}
        >
          <div className="flex items-center justify-between">
            <XCircle className="h-8 w-8 text-gray-400" />
            <span className="text-2xl font-bold text-gray-600">{counts.cancelled}</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">Cancelled</p>
        </button>

        {/* Pending Approval card - only shown if user can approve tasks (Issue #150) */}
        {pendingApprovalCount > 0 && (
          <button
            onClick={() => handleStatusFilter('pending-approval')}
            className={`card p-4 text-left transition-all border-2 border-purple-200 ${statusFilter === 'pending-approval' ? 'ring-2 ring-purple-500' : ''}`}
          >
            <div className="flex items-center justify-between">
              <UserCheck className="h-8 w-8 text-purple-500" />
              <span className="text-2xl font-bold text-purple-600">{pendingApprovalCount}</span>
            </div>
            <p className="text-sm text-purple-600 font-medium mt-2">Pending Approval</p>
          </button>
        )}

        <div className="card p-4 text-left">
          <div className="flex items-center justify-between">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <span className="text-2xl font-bold text-red-600">{counts.overdue}</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">Overdue</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="input"
              >
                <option value="">All Types</option>
                {Object.entries(typeConfig).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Priority</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="input"
              >
                <option value="">All Priorities</option>
                {Object.entries(priorityConfig).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Assigned To</label>
              <select
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value)}
                className="input"
              >
                <option value="">All Users</option>
                <option value="me">My Tasks</option>
                <option value="unassigned">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="input"
              >
                <option value="">All Sources</option>
                <option value="manual">Manual</option>
                <option value="auto-frequency">Auto (Frequency)</option>
                <option value="auto-reminder">Auto (Reminder)</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Toolbar */}
      {selectedTasks.size > 0 && (
        <div className="card p-4 bg-primary-50 border-primary-200 sticky top-0 z-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="font-medium text-primary-700">
                {selectedTasks.size} task{selectedTasks.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-primary-600 hover:text-primary-800 underline"
              >
                Clear selection
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Status dropdown */}
              <div className="relative group">
                <button
                  disabled={bulkActionLoading}
                  className="btn-secondary text-sm py-1.5 px-3"
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Set Status
                  <ChevronDown className="h-3 w-3 ml-1" />
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                  <button
                    onClick={() => handleBulkStatusUpdate('open')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Clock className="h-4 w-4 text-blue-500" />
                    Open
                  </button>
                  <button
                    onClick={() => handleBulkStatusUpdate('in-progress')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <PlayCircle className="h-4 w-4 text-amber-500" />
                    In Progress
                  </button>
                  <button
                    onClick={() => handleBulkStatusUpdate('completed')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Completed
                  </button>
                  <button
                    onClick={() => handleBulkStatusUpdate('cancelled')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <XCircle className="h-4 w-4 text-gray-500" />
                    Cancelled
                  </button>
                </div>
              </div>

              {/* Priority dropdown */}
              <div className="relative group">
                <button
                  disabled={bulkActionLoading}
                  className="btn-secondary text-sm py-1.5 px-3"
                >
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Set Priority
                  <ChevronDown className="h-3 w-3 ml-1" />
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                  <button
                    onClick={() => handleBulkPriorityUpdate('critical')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2" />
                    Critical
                  </button>
                  <button
                    onClick={() => handleBulkPriorityUpdate('high')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    <span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-2" />
                    High
                  </button>
                  <button
                    onClick={() => handleBulkPriorityUpdate('medium')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-2" />
                    Medium
                  </button>
                  <button
                    onClick={() => handleBulkPriorityUpdate('low')}
                    disabled={bulkActionLoading}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    <span className="inline-block w-2 h-2 rounded-full bg-gray-400 mr-2" />
                    Low
                  </button>
                </div>
              </div>

              {/* Assign button */}
              <button
                onClick={() => setShowBulkAssignModal(true)}
                disabled={bulkActionLoading}
                className="btn-secondary text-sm py-1.5 px-3"
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Assign
              </button>

              {/* Delete button */}
              <button
                onClick={() => setShowBulkDeleteConfirm(true)}
                disabled={bulkActionLoading}
                className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </button>
            </div>
          </div>

          {/* Bulk action error message */}
          {bulkActionError && (
            <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {bulkActionError}
            </div>
          )}

          {/* Loading indicator */}
          {bulkActionLoading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-primary-600">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
              Processing...
            </div>
          )}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        loading ? (
          <div className="card flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <TaskCalendar
            tasks={tasks}
            onTaskClick={handleOpenEdit}
            onDateClick={(date) => {
              // Pre-fill the date when creating a task from calendar
              setFormData({
                title: '',
                description: '',
                type: 'custom',
                assigned_to: '',
                priority: 'medium',
                due_date: date.toISOString().split('T')[0],
                related_control_id: '',
                estimated_hours: '',
                approval_required: false,
                approver_id: '',
                approver_role: '',
              })
              setFormError('')
              setShowCreateModal(true)
            }}
          />
        )
      )}

      {/* Kanban View (Issue #107) */}
      {viewMode === 'kanban' && (
        <TaskKanban
          tasks={tasks}
          loading={loading}
          onTaskClick={handleOpenEdit}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Tasks List */}
      {viewMode === 'list' && (
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No tasks found</p>
            <p className="text-sm text-gray-400 mt-1">
              {statusFilter || typeFilter || priorityFilter ? 'Try changing your filters' : 'Create your first task to get started'}
            </p>
            <button
              onClick={handleOpenCreate}
              className="btn-primary mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Task
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {/* Select All Header */}
            <div className="px-4 py-3 bg-gray-50 flex items-center gap-3 border-b">
              <button
                onClick={handleSelectAll}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title={selectedTasks.size === tasks.length ? 'Deselect all' : 'Select all'}
              >
                {selectedTasks.size === 0 ? (
                  <Square className="h-5 w-5 text-gray-400" />
                ) : selectedTasks.size === tasks.length ? (
                  <CheckSquare className="h-5 w-5 text-primary-600" />
                ) : (
                  <MinusSquare className="h-5 w-5 text-primary-600" />
                )}
              </button>
              <span className="text-sm text-gray-600">
                {selectedTasks.size > 0
                  ? `${selectedTasks.size} of ${tasks.length} selected`
                  : `${tasks.length} tasks`}
              </span>
            </div>

            {tasks.map((task) => {
              const taskStatus = statusConfig[task.status] || statusConfig.open
              const TaskTypeIcon = typeConfig[task.type]?.icon || ClipboardList
              const StatusIcon = taskStatus.icon
              const overdue = isOverdue(task)
              const isSelected = selectedTasks.has(task.id)

              return (
                <div
                  key={task.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${overdue ? 'bg-red-50' : ''} ${isSelected ? 'bg-primary-50' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => handleToggleTaskSelection(task.id)}
                      className="mt-1 p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                    >
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-primary-600" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {/* Priority badge */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${priorityConfig[task.priority]?.color || ''}`}>
                          {priorityConfig[task.priority]?.label}
                        </span>
                        {/* Status badge */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${taskStatus.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {taskStatus.label}
                        </span>
                        {/* Overdue indicator */}
                        {overdue && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Overdue
                          </span>
                        )}
                        {/* Auto-generated indicator */}
                        {task.source === 'auto-frequency' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Auto
                          </span>
                        )}
                        {/* Jira sync indicator */}
                        {task.jira_issue_key && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                            task.jira_sync_status === 'synced'
                              ? 'bg-blue-100 text-blue-800 border-blue-200'
                              : task.jira_sync_status === 'error'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                          }`}>
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Jira {task.jira_sync_status === 'synced' ? '✓' : task.jira_sync_status === 'error' ? '!' : '...'}
                          </span>
                        )}
                        {/* Type */}
                        <span className="text-xs text-gray-500 flex items-center">
                          <TaskTypeIcon className="h-3 w-3 mr-1" />
                          {typeConfig[task.type]?.label || 'Task'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-gray-900">{task.title}</h3>
                        {/* Jira external link */}
                        {task.jira_issue_key && task.jira_issue_url && (
                          <a
                            href={task.jira_issue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View ${task.jira_issue_key} in Jira`}
                            className="text-blue-600 hover:text-blue-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>

                      {task.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                        {/* Due date */}
                        <div className={`flex items-center ${overdue ? 'text-red-600 font-medium' : ''}`}>
                          <Calendar className="h-4 w-4 mr-1" />
                          {task.due_date ? (overdue ? 'Was due: ' : 'Due: ') + formatDate(task.due_date) : 'No due date'}
                        </div>

                        {/* Assigned to */}
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          {task.assigned_to_username || 'Unassigned'}
                        </div>

                        {/* Evidence count */}
                        {(task.evidence_count !== undefined && task.evidence_count > 0) && (
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 mr-1" />
                            {task.evidence_count} evidence
                          </div>
                        )}

                        {/* Related control */}
                        {task.control_number && (
                          <Link
                            to={`/controls/${task.related_control_id}`}
                            className="flex items-center text-primary-600 hover:underline"
                          >
                            <Shield className="h-4 w-4 mr-1" />
                            {task.control_number}
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      {/* Quick status change */}
                      {task.status !== 'completed' && task.status !== 'cancelled' && (
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task, e.target.value as Task['status'])}
                          className="text-xs border rounded px-2 py-1"
                        >
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}

                      {/* Link evidence */}
                      <button
                        onClick={() => handleOpenLinkEvidence(task)}
                        className="p-2 text-gray-400 hover:text-primary-600"
                        title="Link Evidence"
                      >
                        <LinkIcon className="h-4 w-4" />
                      </button>

                      {/* Timer */}
                      {activeTimer?.task_id === task.id ? (
                        <button
                          onClick={() => handleStopTimer()}
                          className="p-2 text-primary-600 hover:text-primary-700"
                          title="Stop Timer"
                        >
                          <TimerOff className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStartTimer(task)}
                          className="p-2 text-gray-400 hover:text-primary-600"
                          title="Start Timer"
                          disabled={!!activeTimer}
                        >
                          <Play className="h-4 w-4" />
                        </button>
                      )}

                      {/* Time Log */}
                      <button
                        onClick={() => handleOpenTimeModal(task)}
                        className="p-2 text-gray-400 hover:text-primary-600"
                        title="Log Time"
                      >
                        <Clock className="h-4 w-4" />
                      </button>

                      {/* Comments (Issue #90) */}
                      <button
                        onClick={() => handleOpenComments(task)}
                        className="p-2 text-gray-400 hover:text-primary-600"
                        title="View Comments"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>

                      {/* Attachments (Issue #98) */}
                      <button
                        onClick={() => handleOpenAttachments(task)}
                        className="p-2 text-gray-400 hover:text-primary-600"
                        title="Attachments"
                      >
                        <Paperclip className="h-4 w-4" />
                      </button>

                      {/* Approval Workflow Actions (Issue #150) */}
                      {/* Submit for Approval - show if task requires approval and is in-progress or rejected */}
                      {task.approval_required === 1 &&
                        (task.status === 'in-progress' || task.status === 'rejected') && (
                        <button
                          onClick={() => handleSubmitForApproval(task)}
                          className="p-2 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded"
                          title="Submit for Approval"
                          disabled={approvalLoading}
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      )}

                      {/* Approve/Reject buttons - show if task is pending approval and user can approve */}
                      {task.status === 'pending-approval' &&
                        pendingApprovalTasks.some(t => t.id === task.id) && (
                        <>
                          <button
                            onClick={() => handleOpenApprovalModal(task, 'approve')}
                            className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded"
                            title="Approve Task"
                          >
                            <ThumbsUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleOpenApprovalModal(task, 'reject')}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Reject Task"
                          >
                            <ThumbsDown className="h-4 w-4" />
                          </button>
                        </>
                      )}

                      {/* View Approval History - show if task has approval history */}
                      {task.approval_history_count && task.approval_history_count > 0 && (
                        <button
                          onClick={() => handleViewApprovalHistory(task)}
                          className="p-2 text-gray-400 hover:text-purple-600"
                          title="View Approval History"
                        >
                          <History className="h-4 w-4" />
                        </button>
                      )}

                      {/* Edit */}
                      <button
                        onClick={() => handleOpenEdit(task)}
                        className="p-2 text-gray-400 hover:text-primary-600"
                        title="Edit Task"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>

                      {/* Delete (admin or creator only) */}
                      {(user?.role === 'admin' || task.created_by === user?.id) && (
                        <button
                          onClick={() => handleOpenDelete(task)}
                          className="p-2 text-gray-400 hover:text-red-600"
                          title="Delete Task"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {showCreateModal ? 'Create Task' : 'Edit Task'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  setSelectedTask(null)
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {formError}
                </div>
              )}

              <div>
                <label className="label">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input"
                  placeholder="Task title"
                />
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input min-h-[100px]"
                  placeholder="Task description (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Task['type'] })}
                    className="input"
                  >
                    {Object.entries(typeConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as Task['priority'] })}
                    className="input"
                  >
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="input"
                  />
                </div>

                <div>
                  <label className="label">Assigned To</label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    className="input"
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.username}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Related Control</label>
                <select
                  value={formData.related_control_id}
                  onChange={(e) => setFormData({ ...formData, related_control_id: e.target.value })}
                  className="input"
                >
                  <option value="">None</option>
                  {controls.map((c) => (
                    <option key={c.id} value={c.id}>{c.control_number}: {c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Estimated Hours</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.estimated_hours}
                  onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                  className="input"
                  placeholder="e.g., 2.5"
                />
                <p className="text-xs text-gray-500 mt-1">For time tracking comparison</p>
              </div>

              {/* Approval Workflow Settings (Issue #150) */}
              <div className="col-span-2 border-t pt-4 mt-2">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="approval_required"
                    checked={formData.approval_required}
                    onChange={(e) => setFormData({
                      ...formData,
                      approval_required: e.target.checked,
                      // Reset approver fields if unchecked
                      approver_id: e.target.checked ? formData.approver_id : '',
                      approver_role: e.target.checked ? formData.approver_role : '',
                    })}
                    className="mr-2"
                  />
                  <label htmlFor="approval_required" className="text-sm font-medium text-gray-700">
                    Requires Approval Before Completion
                  </label>
                </div>

                {formData.approval_required && (
                  <div className="grid grid-cols-2 gap-4 bg-purple-50 p-3 rounded-lg">
                    <div>
                      <label className="label">Specific Approver</label>
                      <select
                        value={formData.approver_id}
                        onChange={(e) => setFormData({
                          ...formData,
                          approver_id: e.target.value,
                          // Clear role if specific approver is selected
                          approver_role: e.target.value ? '' : formData.approver_role,
                        })}
                        className="input"
                        disabled={!!formData.approver_role}
                      >
                        <option value="">Select approver...</option>
                        {users.filter(u => u.id !== formData.assigned_to).map((u) => (
                          <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Designate a specific person</p>
                    </div>

                    <div>
                      <label className="label">Or Approve by Role</label>
                      <select
                        value={formData.approver_role}
                        onChange={(e) => setFormData({
                          ...formData,
                          approver_role: e.target.value as '' | 'admin' | 'contributor',
                          // Clear specific approver if role is selected
                          approver_id: e.target.value ? '' : formData.approver_id,
                        })}
                        className="input"
                        disabled={!!formData.approver_id}
                      >
                        <option value="">Select role...</option>
                        <option value="admin">Any Admin</option>
                        <option value="contributor">Any Admin or Contributor</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Anyone with this role can approve</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  setSelectedTask(null)
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={showCreateModal ? handleCreate : handleUpdate}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? 'Saving...' : showCreateModal ? 'Create' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Delete Task</h3>
              <p className="text-gray-500 text-center mb-6">
                Are you sure you want to delete "{selectedTask.title}"? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setSelectedTask(null)
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {saving ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Evidence Modal */}
      {showLinkEvidenceModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Link Evidence to Task</h2>
              <button
                onClick={() => {
                  setShowLinkEvidenceModal(false)
                  setSelectedTask(null)
                  setLinkedEvidence([])
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Currently Linked Evidence */}
              {linkedEvidence.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Linked Evidence ({linkedEvidence.length})</h3>
                  <div className="space-y-2">
                    {linkedEvidence.map((e) => (
                      <div key={e.evidence_id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{e.file_name || e.note || 'Evidence'}</p>
                          <p className="text-xs text-gray-500">
                            {e.control_number && `${e.control_number} - `}
                            Linked {formatDateTime(e.linked_at)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleUnlinkEvidence(e.evidence_id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Unlink"
                        >
                          <Unlink className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Evidence */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Available Evidence</h3>
                <div className="space-y-2">
                  {evidence
                    .filter(e => !linkedEvidence.find(le => le.evidence_id === e.id))
                    .map((e) => (
                      <div key={e.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div>
                          <p className="font-medium text-sm">{e.file_name || e.note || 'Evidence'}</p>
                          <p className="text-xs text-gray-500">{formatDate(e.timestamp)}</p>
                        </div>
                        <button
                          onClick={() => handleLinkEvidence(e.id)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded"
                          title="Link"
                        >
                          <LinkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  {evidence.filter(e => !linkedEvidence.find(le => le.evidence_id === e.id)).length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">No available evidence to link</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end p-4 border-t">
              <button
                onClick={() => {
                  setShowLinkEvidenceModal(false)
                  setSelectedTask(null)
                  setLinkedEvidence([])
                }}
                className="btn-secondary"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">Delete {selectedTasks.size} Task{selectedTasks.size !== 1 ? 's' : ''}</h3>
              <p className="text-gray-500 text-center mb-6">
                Are you sure you want to delete {selectedTasks.size} selected task{selectedTasks.size !== 1 ? 's' : ''}?
                This action cannot be undone.
              </p>
              {bulkActionError && (
                <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {bulkActionError}
                </div>
              )}
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setShowBulkDeleteConfirm(false)
                    setBulkActionError('')
                  }}
                  disabled={bulkActionLoading}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
                >
                  {bulkActionLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Tasks'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                Assign {selectedTasks.size} Task{selectedTasks.size !== 1 ? 's' : ''}
              </h2>
              <button
                onClick={() => {
                  setShowBulkAssignModal(false)
                  setBulkActionError('')
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a user to assign the selected tasks to, or unassign them.
              </p>

              {bulkActionError && (
                <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {bulkActionError}
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {/* Unassign option */}
                <button
                  onClick={() => handleBulkAssign(null)}
                  disabled={bulkActionLoading}
                  className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 flex items-center gap-3 disabled:opacity-50"
                >
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Unassign</p>
                    <p className="text-xs text-gray-500">Remove assignment from tasks</p>
                  </div>
                </button>

                {/* User list */}
                {users.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleBulkAssign(u.id)}
                    disabled={bulkActionLoading}
                    className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 flex items-center gap-3 disabled:opacity-50"
                  >
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-600">
                        {u.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{u.username}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                  </button>
                ))}
              </div>

              {bulkActionLoading && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-primary-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
                  Assigning...
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 border-t">
              <button
                onClick={() => {
                  setShowBulkAssignModal(false)
                  setBulkActionError('')
                }}
                disabled={bulkActionLoading}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal (Issue #150) */}
      {showApprovalModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center">
                {approvalAction === 'approve' ? (
                  <ThumbsUp className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <ThumbsDown className="h-5 w-5 text-red-500 mr-2" />
                )}
                <h2 className="text-lg font-semibold">
                  {approvalAction === 'approve' ? 'Approve Task' : 'Reject Task'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowApprovalModal(false)
                  setSelectedTask(null)
                  setApprovalComments('')
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{selectedTask.title}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Assigned to: {selectedTask.assigned_to_username || 'Unassigned'}
                </p>
              </div>

              <div>
                <label className="label">
                  Comments {approvalAction === 'reject' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  placeholder={
                    approvalAction === 'approve'
                      ? 'Optional approval comments...'
                      : 'Please explain why this task is being rejected...'
                  }
                  rows={3}
                  className="input"
                />
                {approvalAction === 'reject' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Comments are required when rejecting a task
                  </p>
                )}
              </div>

              {approvalAction === 'approve' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="autoComplete"
                    checked={autoCompleteOnApproval}
                    onChange={(e) => setAutoCompleteOnApproval(e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="autoComplete" className="text-sm text-gray-700">
                    Mark task as completed after approval
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowApprovalModal(false)
                  setSelectedTask(null)
                  setApprovalComments('')
                }}
                disabled={approvalLoading}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={approvalAction === 'approve' ? handleApproveTask : handleRejectTask}
                disabled={approvalLoading || (approvalAction === 'reject' && !approvalComments.trim())}
                className={approvalAction === 'approve' ? 'btn-success' : 'btn-danger'}
              >
                {approvalLoading
                  ? 'Processing...'
                  : approvalAction === 'approve'
                  ? 'Approve'
                  : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval History Modal (Issue #150) */}
      {showApprovalHistoryModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center">
                <History className="h-5 w-5 text-purple-500 mr-2" />
                <h2 className="text-lg font-semibold">Approval History</h2>
              </div>
              <button
                onClick={() => {
                  setShowApprovalHistoryModal(false)
                  setSelectedTask(null)
                  setTaskApprovals([])
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="font-medium text-gray-900">{selectedTask.title}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Current Status: <span className={`font-medium ${statusConfig[selectedTask.status]?.color || ''} px-2 py-0.5 rounded text-xs`}>
                    {statusConfig[selectedTask.status]?.label || selectedTask.status}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {taskApprovals.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No approval history yet.</p>
              ) : (
                <div className="space-y-3">
                  {taskApprovals.map((approval) => (
                    <div
                      key={approval.id}
                      className={`p-3 rounded-lg border ${
                        approval.action === 'approved'
                          ? 'bg-green-50 border-green-200'
                          : approval.action === 'rejected'
                          ? 'bg-red-50 border-red-200'
                          : approval.action === 'submitted'
                          ? 'bg-purple-50 border-purple-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center">
                          {approval.action === 'approved' && <ThumbsUp className="h-4 w-4 text-green-600 mr-2" />}
                          {approval.action === 'rejected' && <ThumbsDown className="h-4 w-4 text-red-600 mr-2" />}
                          {approval.action === 'submitted' && <Send className="h-4 w-4 text-purple-600 mr-2" />}
                          {approval.action === 'reverted' && <RefreshCw className="h-4 w-4 text-gray-600 mr-2" />}
                          <span className="font-medium capitalize">{approval.action}</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(approval.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        by {approval.approver_username || 'Unknown'}
                      </p>
                      {approval.comments && (
                        <p className="text-sm text-gray-700 mt-2 bg-white p-2 rounded">
                          "{approval.comments}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowApprovalHistoryModal(false)
                  setSelectedTask(null)
                  setTaskApprovals([])
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Entry Modal */}
      {showTimeModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">Time Tracking</h2>
                <p className="text-sm text-gray-500">{selectedTask.title}</p>
              </div>
              <button onClick={() => { setShowTimeModal(false); setSelectedTask(null) }} className="p-2 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-6">
              {timeSummary && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Total Time</p><p className="text-xl font-semibold">{formatDuration(timeSummary.total_minutes)}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Estimated</p><p className="text-xl font-semibold">{timeSummary.estimated_hours ? `${timeSummary.estimated_hours}h` : '-'}</p></div>
                  <div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Variance</p><p className={`text-xl font-semibold ${timeSummary.estimated_hours && timeSummary.total_hours > timeSummary.estimated_hours ? 'text-red-600' : 'text-green-600'}`}>{timeSummary.estimated_hours ? `${timeSummary.total_hours > timeSummary.estimated_hours ? '+' : ''}${(timeSummary.total_hours - timeSummary.estimated_hours).toFixed(1)}h` : '-'}</p></div>
                </div>
              )}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium mb-3">Log Time</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><label className="text-xs text-gray-500">Date</label><input type="date" value={timeEntryForm.date} onChange={(e) => setTimeEntryForm({ ...timeEntryForm, date: e.target.value })} className="input mt-1" /></div>
                  <div><label className="text-xs text-gray-500">Hours</label><input type="number" min="0" max="24" value={timeEntryForm.hours} onChange={(e) => setTimeEntryForm({ ...timeEntryForm, hours: parseInt(e.target.value) || 0 })} className="input mt-1" /></div>
                  <div><label className="text-xs text-gray-500">Minutes</label><input type="number" min="0" max="59" step="5" value={timeEntryForm.minutes} onChange={(e) => setTimeEntryForm({ ...timeEntryForm, minutes: parseInt(e.target.value) || 0 })} className="input mt-1" /></div>
                  <div className="flex items-end"><button onClick={handleAddTimeEntry} className="btn-primary w-full">Add Entry</button></div>
                </div>
                <div className="mt-3"><label className="text-xs text-gray-500">Description (optional)</label><input type="text" value={timeEntryForm.description} onChange={(e) => setTimeEntryForm({ ...timeEntryForm, description: e.target.value })} className="input mt-1" placeholder="What did you work on?" /></div>
              </div>
              <div>
                <h3 className="font-medium mb-3">Time Entries ({timeEntries.length})</h3>
                {timeEntries.length === 0 ? <p className="text-gray-500 text-sm">No time entries yet</p> : (
                  <div className="space-y-2">{timeEntries.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded ${entry.entry_type === 'timer' ? 'bg-primary-100' : 'bg-gray-100'}`}>{entry.entry_type === 'timer' ? <Timer className="h-4 w-4 text-primary-600" /> : <Clock className="h-4 w-4 text-gray-600" />}</div>
                        <div><p className="font-medium">{formatDuration(entry.duration_minutes || 0)}</p><p className="text-xs text-gray-500">{formatDate(entry.started_at)} by {entry.user_username || 'Unknown'}</p>{entry.description && <p className="text-sm text-gray-600 mt-1">{entry.description}</p>}</div>
                      </div>
                      <button onClick={() => handleDeleteTimeEntry(entry.id)} className="p-2 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time Report Modal */}
      {showTimeReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b"><h2 className="text-lg font-semibold">Time Report</h2><button onClick={() => setShowTimeReportModal(false)} className="p-2 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button></div>
            <div className="p-4 space-y-6">
              <div className="flex flex-wrap gap-3 items-end">
                <div><label className="text-xs text-gray-500">Start Date</label><input type="date" value={timeReportFilters.start} onChange={(e) => setTimeReportFilters({ ...timeReportFilters, start: e.target.value })} className="input mt-1" /></div>
                <div><label className="text-xs text-gray-500">End Date</label><input type="date" value={timeReportFilters.end} onChange={(e) => setTimeReportFilters({ ...timeReportFilters, end: e.target.value })} className="input mt-1" /></div>
                <div><label className="text-xs text-gray-500">User</label><select value={timeReportFilters.user_id} onChange={(e) => setTimeReportFilters({ ...timeReportFilters, user_id: e.target.value })} className="input mt-1"><option value="">All Users</option>{users.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}</select></div>
                <button onClick={handleOpenTimeReport} className="btn-primary"><RefreshCw className="h-4 w-4 mr-2" />Refresh</button>
                {timeReport && <button onClick={handleExportTimeReport} className="btn-secondary"><Download className="h-4 w-4 mr-2" />Export CSV</button>}
              </div>
              {timeReport && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-primary-50 rounded-lg"><p className="text-sm text-primary-600">Total Time</p><p className="text-2xl font-bold text-primary-900">{timeReport.total_hours.toFixed(1)}h</p><p className="text-sm text-primary-600">{timeReport.entry_count} entries</p></div>
                    <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Tasks Tracked</p><p className="text-2xl font-bold text-gray-900">{timeReport.by_task.length}</p></div>
                    <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Team Members</p><p className="text-2xl font-bold text-gray-900">{timeReport.by_user.length}</p></div>
                  </div>
                  <div><h3 className="font-medium mb-3">Time by Task</h3><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="bg-gray-50 text-left"><th className="p-3">Task</th><th className="p-3">Status</th><th className="p-3 text-right">Estimated</th><th className="p-3 text-right">Actual</th><th className="p-3 text-right">Variance</th></tr></thead><tbody>{timeReport.by_task.map((task) => (<tr key={task.task_id} className="border-t"><td className="p-3">{task.task_title}</td><td className="p-3"><span className={`text-xs px-2 py-1 rounded ${statusConfig[task.task_status]?.color || 'bg-gray-100'}`}>{statusConfig[task.task_status]?.label || task.task_status}</span></td><td className="p-3 text-right">{task.estimated_hours ? `${task.estimated_hours}h` : '-'}</td><td className="p-3 text-right">{task.total_hours.toFixed(1)}h</td><td className={`p-3 text-right ${task.variance_hours !== null && task.variance_hours > 0 ? 'text-red-600' : task.variance_hours !== null ? 'text-green-600' : ''}`}>{task.variance_hours !== null ? `${task.variance_hours > 0 ? '+' : ''}${task.variance_hours.toFixed(1)}h` : '-'}</td></tr>))}</tbody></table></div></div>
                  <div><h3 className="font-medium mb-3">Time by User</h3><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{timeReport.by_user.map((u) => (<div key={u.user_id} className="p-4 border rounded-lg"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center"><User className="h-5 w-5 text-primary-600" /></div><div><p className="font-medium">{u.username}</p><p className="text-sm text-gray-500">{u.total_hours.toFixed(1)}h ({u.entry_count} entries)</p></div></div></div>))}</div></div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Task Comments Modal (Issue #90) */}
      {showCommentsModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary-600" />
                  Comments
                </h2>
                <p className="text-sm text-gray-500">{selectedTask.title}</p>
              </div>
              <button
                onClick={() => {
                  setShowCommentsModal(false)
                  setSelectedTask(null)
                  setTaskComments([])
                  setNewComment('')
                  setEditingCommentId(null)
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {taskComments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No comments yet</p>
                  <p className="text-sm">Be the first to add a comment!</p>
                </div>
              ) : (
                taskComments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-primary-700">
                        {getAuthorInitials(comment)}
                      </span>
                    </div>

                    {/* Comment Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          {getAuthorDisplayName(comment)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatCommentTime(comment.created_at)}
                        </span>
                        {comment.updated_at > comment.created_at && (
                          <span className="text-xs text-gray-400">(edited)</span>
                        )}
                      </div>

                      {editingCommentId === comment.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingCommentContent}
                            onChange={(e) => setEditingCommentContent(e.target.value)}
                            className="input min-h-[80px] w-full"
                            placeholder="Edit your comment..."
                            disabled={commentsLoading}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEditComment(comment.id)}
                              className="btn-primary text-sm py-1 px-3"
                              disabled={commentsLoading || !editingCommentContent.trim()}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEditComment}
                              className="btn-secondary text-sm py-1 px-3"
                              disabled={commentsLoading}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="group">
                          <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>

                          {/* Actions - only show for own comments or admin */}
                          {(comment.user_id === user?.id || user?.role === 'admin') && (
                            <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleStartEditComment(comment)}
                                className="text-xs text-gray-500 hover:text-primary-600"
                                disabled={commentsLoading}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-xs text-gray-500 hover:text-red-600"
                                disabled={commentsLoading}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add Comment Input */}
            <div className="border-t p-4">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-primary-600" />
                </div>
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="input min-h-[80px] w-full"
                    placeholder="Add a comment..."
                    disabled={commentsLoading}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        handleAddComment()
                      }
                    }}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      Press Cmd+Enter to submit
                    </span>
                    <button
                      onClick={handleAddComment}
                      className="btn-primary"
                      disabled={commentsLoading || !newComment.trim()}
                    >
                      {commentsLoading ? 'Adding...' : 'Add Comment'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attachments Modal (Issue #98) */}
      {showAttachmentsModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Paperclip className="h-5 w-5 text-primary-600" />
                  Attachments
                </h2>
                <p className="text-sm text-gray-500">{selectedTask.title}</p>
              </div>
              <button
                onClick={() => {
                  setShowAttachmentsModal(false)
                  setSelectedTask(null)
                  setTaskAttachments([])
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Upload Area */}
            {user?.role !== 'viewer' && (
              <div className="p-4 border-b">
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragOver
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleUploadAttachment(e.target.files)}
                    className="hidden"
                    id="attachment-upload"
                  />
                  {attachmentUploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 border-t-transparent" />
                      <span className="text-gray-600">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600 mb-1">
                        Drag and drop files here, or{' '}
                        <label
                          htmlFor="attachment-upload"
                          className="text-primary-600 hover:text-primary-700 cursor-pointer font-medium"
                        >
                          browse
                        </label>
                      </p>
                      <p className="text-xs text-gray-500">Maximum file size: 50MB</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Attachments List */}
            <div className="flex-1 overflow-y-auto p-4">
              {attachmentsLoading && taskAttachments.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
                </div>
              ) : taskAttachments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Paperclip className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No attachments yet</p>
                  {user?.role !== 'viewer' && (
                    <p className="text-sm">Upload files to attach them to this task</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {taskAttachments.map((attachment) => {
                    const FileIcon = getFileIcon(attachment.mime_type)
                    return (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center flex-shrink-0">
                            <FileIcon className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {attachment.filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(attachment.file_size)} &bull;{' '}
                              {attachment.uploaded_by_username || 'Unknown'} &bull;{' '}
                              {formatDate(attachment.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white rounded"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          {(attachment.uploaded_by === user?.id || user?.role === 'admin') && (
                            <button
                              onClick={() => handleDeleteAttachment(attachment.id)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-white rounded"
                              title="Delete"
                              disabled={attachmentsLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 flex justify-end">
              <button
                onClick={() => {
                  setShowAttachmentsModal(false)
                  setSelectedTask(null)
                  setTaskAttachments([])
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
